import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import {
  FiEdit2,
  FiX,
  FiCheck,
  FiUser,
  FiFileText,
  FiMail,
  FiPhone,
  FiInfo,
  FiCamera,
} from 'react-icons/fi';
import BookingHistory from './BookingHistory';
import Cookies from 'js-cookie';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import HelpSupportForm from './HelpSupportForm';
import countries from '../CHECK-IN/Walk-In/countries';
import Webcam from 'react-webcam';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const Profile = () => {
  const [activeTab, setActiveTab] = useState('guestDetails');
  const [guestDetails, setGuestDetails] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    emailid: '',
    phonenumber: '',
    client_type: 'Leisure',
    companyname: '',
    employeeid: '',
    companyid: '',
    country: '',
    countrycode: '+91',
  });
  const [validationErrors, setValidationErrors] = useState({
    firstname: null,
    lastname: null,
  });

  // Profile Photo States
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [isPhotoPopupOpen, setIsPhotoPopupOpen] = useState(false);

  // Submitting state for save button
  const [isSubmitting, setIsSubmitting] = useState(false);

  // OTP Verification States
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [otpType, setOtpType] = useState('email');
  const [resendTimer, setResendTimer] = useState(300);
  const [canResend, setCanResend] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);
  const [originalContactData, setOriginalContactData] = useState(null);

  // Company search state
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);

  // Country dropdown state
  const [selectedCountry, setSelectedCountry] = useState(
    countries.find((country) => country.name === 'India') || countries[0]
  );
  const [isCountryOpen, setIsCountryOpen] = useState(false);

  // Webcam ref
  const webcamRef = useRef(null);

  const navigate = useNavigate();

  // Fetch companies for autocomplete
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const accessToken = Cookies.get('access_token');
        if (!accessToken) return;

        const response = await fetch(`${BQ_BASE_URL}/bq/api/companies`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch companies');
        }

        const data = await response.json();
        if (data && data.companies) {
          setAllCompanies(data.companies);
          setFilteredCompanies(data.companies);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };

    fetchCompanies();
  }, []);

  // Filter companies based on search term
  useEffect(() => {
    if (companySearchTerm && companySearchTerm.length > 0) {
      const searchLower = companySearchTerm.toLowerCase();
      const filtered = allCompanies.filter(
        (company) =>
          company.companyname.toLowerCase().includes(searchLower) ||
          company.companyid?.toLowerCase().includes(searchLower)
      );
      setFilteredCompanies(filtered);
      setIsCompanyDropdownOpen(true);
    } else {
      setFilteredCompanies([]);
      setIsCompanyDropdownOpen(false);
    }
  }, [companySearchTerm, allCompanies]);

  // Timer for OTP resend
  useEffect(() => {
    if (showOtpModal && !canResend) {
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [showOtpModal, canResend]);

  // Helper function to clean phone numbers
  const cleanPhoneNumber = (phone) => {
    if (!phone) return '';
    return phone.toString().replace(/\D/g, '');
  };

  const decryptUserData = async (encryptedData) => {
    try {
      const decryptedData = { ...encryptedData };
      const fieldsToDecrypt = {};

      if (encryptedData.email && encryptedData.email.length > 50) {
        fieldsToDecrypt.email = encryptedData.email;
      } else if (encryptedData.email) {
        decryptedData.email = encryptedData.email;
      }

      const phoneStr = String(encryptedData.phone_number || '');
      if (phoneStr && !/^[\d\+\-\s\(\)]+$/.test(phoneStr)) {
        fieldsToDecrypt.phone_number = encryptedData.phone_number;
      } else if (phoneStr) {
        decryptedData.phone_number = encryptedData.phone_number;
      }

      if (encryptedData.country_code && encryptedData.country_code.length > 10) {
        fieldsToDecrypt.country_code = encryptedData.country_code;
      } else if (encryptedData.country_code) {
        decryptedData.country_code = encryptedData.country_code;
      }

      if (Object.keys(fieldsToDecrypt).length === 0) {
        return decryptedData;
      }

      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          encrypted_fields: fieldsToDecrypt,
        }),
      });

      if (response.status === 401) {
        return decryptedData;
      }

      if (!response.ok) {
        throw new Error(`Decryption failed: ${response.statusText}`);
      }

      const decryptedResult = await response.json();
      return {
        ...decryptedData,
        ...decryptedResult,
      };
    } catch (error) {
      console.error('Decryption error:', error);
      return encryptedData;
    }
  };

  const fetchGuestData = async () => {
    setIsLoading(true);

    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        toast.error('Session expired. Please login again.');
        setIsLoading(false);
        return;
      }

      const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
      if (!guestId) {
        toast.error('No guest ID found. Please login again.');
        setIsLoading(false);
        return;
      }

      const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/guests/${guestId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!profileResponse.ok) {
        throw new Error(`Profile API Error: ${profileResponse.statusText}`);
      }

      const profileData = await profileResponse.json();
      const guest = profileData.guest;

      const isDecrypted = guest.emailid && guest.emailid.length < 50;

      let guestData;
      if (isDecrypted) {
        guestData = {
          guest_id: guestId,
          firstname: guest.firstname || '',
          lastname: guest.lastname || '',
          emailid: guest.emailid || '',
          phonenumber: guest.phonenumber || '',
          countrycode: guest.countrycode || '+91',
          client_type: guest.clienttype || 'Leisure',
          profilephoto: guest.profile_photo || null,
          aadhar_file: guest.aadhar_image_url || null,
          companyname: guest.companyname || '',
          employeeid: guest.employeeid || '',
          companyid: guest.companyid || '',
          country: guest.country || 'India',
        };
      } else {
        const decryptedFields = await decryptUserData({
          email: guest.emailid,
          phone_number: guest.phonenumber,
          country_code: guest.countrycode,
        });

        guestData = {
          guest_id: guestId,
          firstname: guest.firstname || '',
          lastname: guest.lastname || '',
          emailid: decryptedFields.email || guest.emailid || '',
          phonenumber: decryptedFields.phone_number || guest.phonenumber || '',
          countrycode: decryptedFields.country_code || guest.countrycode || '+91',
          client_type: guest.clienttype || 'Leisure',
          profilephoto: guest.profile_photo || null,
          aadhar_file: guest.aadhar_image_url || null,
          companyname: guest.companyname || '',
          employeeid: guest.employeeid || '',
          companyid: guest.companyid || '',
          country: guest.country || 'India',
        };
      }

      setGuestDetails(guestData);

      // Set profile preview if photo exists
      if (guestData.profilephoto) {
        setProfilePreview(guestData.profilephoto);
      }

      setFormData({
        firstname: guestData.firstname,
        lastname: guestData.lastname,
        emailid: guestData.emailid,
        phonenumber: guestData.phonenumber,
        client_type: guestData.client_type,
        companyname: guestData.companyname,
        employeeid: guestData.employeeid,
        companyid: guestData.companyid,
        country: guestData.country,
        countrycode: guestData.countrycode,
      });

      // Set selected country using dialCode
      let guestCountry = countries.find(
        (country) =>
          country.name === guestData.country ||
          country.dialCode === (guestData.countrycode || '+91')
      );
      if (!guestCountry) {
        guestCountry = countries.find((country) => country.dialCode === '+91') || countries[0];
      }
      setSelectedCountry(guestCountry);

      // Fetch bookings
      const bookingsResponse = await fetch(
        `${BQ_BASE_URL}/bq/api/profile/bookings/?guestid=${guestId}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (bookingsResponse.ok) {
        const bookingsData = await bookingsResponse.json();
        setBookings(bookingsData.bookings || []);
      }
    } catch (err) {
      console.error('Error in fetchGuestData:', err);
      toast.error(`Failed to load profile: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getGuestIdFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (e) {
      console.error('Error parsing token:', e);
      return null;
    }
  };

  useEffect(() => {
    fetchGuestData();
  }, []);

  const validateName = (name) => {
    if (!name.trim()) {
      return 'This field is required';
    }
    if (name.trim().length < 2) {
      return 'Must be at least 2 characters long';
    }
    if (/\d/.test(name)) {
      return 'Should not contain numbers';
    }
    return null;
  };

  // ==================== PROFILE PHOTO FUNCTIONS ====================

  const captureImage = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      toast.error('Failed to capture image');
      return;
    }
    setProfilePreview(imageSrc);
    setProfileImage(dataURLToBlob(imageSrc));
    setIsPhotoPopupOpen(false);
  };

  const dataURLToBlob = (dataURL) => {
    const byteString = atob(dataURL.split(',')[1]);
    const mimeString = 'image/jpeg';
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePreview(reader.result);
      setProfileImage(file);
      setIsPhotoPopupOpen(false);
    };
    reader.readAsDataURL(file);
  };

  const openPhotoPopup = () => {
    setIsPhotoPopupOpen(true);
  };

  const closePhotoPopup = () => {
    setIsPhotoPopupOpen(false);
  };

  // ==================== OTP FUNCTIONS ====================

  const openEditModal = () => {
    if (guestDetails) {
      setFormData({
        firstname: guestDetails.firstname || '',
        lastname: guestDetails.lastname || '',
        emailid: guestDetails.emailid || '',
        phonenumber: guestDetails.phonenumber || '',
        client_type: guestDetails.client_type || 'Leisure',
        companyname: guestDetails.companyname || '',
        employeeid: guestDetails.employeeid || '',
        companyid: guestDetails.companyid || '',
        country: guestDetails.country || 'India',
        countrycode: guestDetails.countrycode || '+91',
      });

      if (guestDetails.profilephoto) {
        setProfilePreview(guestDetails.profilephoto);
      } else {
        setProfilePreview(null);
      }
      setProfileImage(null);

      let guestCountry = countries.find(
        (country) =>
          country.name === guestDetails.country ||
          country.dialCode === (guestDetails.countrycode || '+91')
      );
      if (!guestCountry) {
        guestCountry = countries.find((country) => country.dialCode === '+91') || countries[0];
      }
      setSelectedCountry(guestCountry);
    }
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setOtpError('');
    setPendingChanges(null);
    setOriginalContactData(null);
    setProfileImage(null);
    if (guestDetails?.profilephoto) {
      setProfilePreview(guestDetails.profilephoto);
    } else {
      setProfilePreview(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'firstname' || name === 'lastname') {
      const error = validateName(value);
      setValidationErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Update contact and send OTP - Store original data for rollback
  const handleUpdateContact = async (updateData) => {
    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        toast.error('Session expired. Please login again.');
        setIsSubmitting(false);
        return;
      }

      // Store original data for rollback in case OTP fails
      setOriginalContactData({
        email: guestDetails?.emailid || '',
        phone: guestDetails?.phonenumber || '',
        country_code: guestDetails?.countrycode || '+91',
      });

      const cleanedData = {
        email: updateData.email || '',
        phone_number: updateData.phone_number ? String(updateData.phone_number) : undefined,
        country_code: updateData.country_code || '+91',
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/update-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(cleanedData),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.detail || 'Failed to update contact';

        if (typeof errorMessage === 'string') {
          if (errorMessage.includes('Email already registered')) {
            toast.error('This email is already registered with another account.');
          } else if (errorMessage.includes('Phone number already registered')) {
            toast.error('This phone number is already registered with another account.');
          } else {
            toast.error(errorMessage);
          }
        } else {
          toast.error('Failed to update profile. Please try again.');
        }

        setIsSubmitting(false);
        throw new Error(typeof errorMessage === 'string' ? errorMessage : 'Update failed');
      }

      const results = data.results || {};

      // Check what was actually changed
      const emailChanged = formData.emailid !== guestDetails?.emailid;
      const phoneChanged =
        cleanPhoneNumber(formData.phonenumber) !== cleanPhoneNumber(guestDetails?.phonenumber);

      const phoneResult = results.phone_number;
      const emailResult = results.email;

      // Store which field was changed for OTP verification
      const changes = {
        emailChanged: emailChanged && updateData.email,
        phoneChanged: phoneChanged && updateData.phone_number,
        newEmail: updateData.email,
        newPhone: updateData.phone_number,
      };
      setPendingChanges(changes);

      // If phone was changed and verification is required, show phone OTP
      if (phoneChanged && phoneResult?.verification_required) {
        setOtpIdentifier(phoneResult.identifier || updateData.phone_number);
        setOtpType('phone');
        setShowOtpModal(true);
        setResendTimer(300);
        setCanResend(false);
        toast.info('Verification code sent to your new phone number');
        setIsSubmitting(false);
        return data;
      }

      // If email was changed (and has value) and verification is required, show email OTP
      if (emailChanged && updateData.email && emailResult?.verification_required) {
        setOtpIdentifier(emailResult.identifier || updateData.email);
        setOtpType('email');
        setShowOtpModal(true);
        setResendTimer(300);
        setCanResend(false);
        toast.info('Verification code sent to your new email');
        setIsSubmitting(false);
        return data;
      }

      // If no verification required, update was successful
      toast.success('Profile updated successfully!');
      await fetchGuestData();
      setIsEditModalOpen(false);
      setIsSubmitting(false);
      return data;
    } catch (error) {
      console.error('Error updating contact:', error);
      setIsSubmitting(false);
      throw error;
    }
  };

  // Rollback contact changes if OTP verification fails
  const rollbackContactChanges = async () => {
    if (!originalContactData) return;

    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) return;

      // Rollback to original values
      await fetch(`${BQ_BASE_URL}/bq/api/update-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: originalContactData.email || '',
          phone_number: originalContactData.phone ? String(originalContactData.phone) : undefined,
          country_code: originalContactData.country_code || '+91',
        }),
      });

      console.log('Contact rolled back to original values');
    } catch (error) {
      console.error('Error rolling back contact:', error);
    } finally {
      setOriginalContactData(null);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setOtpError('Please enter 6-digit OTP');
      return;
    }

    setIsVerifying(true);

    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        toast.error('Session expired. Please login again.');
        setIsVerifying(false);
        return;
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/verify-email-phone-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          identifier: otpIdentifier,
          otp: otp,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.detail || 'Verification failed';

        // ❌ OTP verification FAILED - Rollback the contact changes
        await rollbackContactChanges();

        if (errorMessage.includes('Invalid OTP')) {
          toast.error('Invalid OTP. Please check and try again.');
        } else if (errorMessage.includes('expired')) {
          toast.error('OTP has expired. Please request a new one.');
        } else {
          toast.error(errorMessage);
        }
        throw new Error(errorMessage);
      }

      // ✅ OTP verification SUCCESS - Keep the changes
      toast.success(`${otpType === 'email' ? 'Email' : 'Phone number'} verified successfully!`);

      setShowOtpModal(false);
      setOtp('');
      setOtpError('');
      setPendingChanges(null);
      setOriginalContactData(null);

      // Update the rest of the profile (name, country, client type)
      await updateProfileFields();

      // Refresh guest data
      await fetchGuestData();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('OTP verification error:', error);
      setOtpError(error.message);
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        toast.error('Session expired. Please login again.');
        return;
      }

      const resendData = {
        email: formData.emailid,
        phone_number: String(formData.phonenumber),
        country_code: selectedCountry.dialCode || '+91',
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/update-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(resendData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend OTP');
      }

      setOtp('');
      setOtpError('');
      toast.success('New OTP sent successfully');
      setResendTimer(300);
      setCanResend(false);
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error(`${error.message || 'Failed to resend OTP'}`);
    }
  };

  // Update profile fields (name, country, client type) - NO OTP
  const updateProfileFields = async () => {
    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        toast.error('Session expired. Please login again.');
        setIsSubmitting(false);
        return;
      }

      const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);

      const profileUpdateData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        country: selectedCountry.name || 'India',
        countrycode: selectedCountry.dialCode || '+91',
        clienttype: formData.client_type,
      };

      if (formData.client_type === 'Corporate') {
        profileUpdateData.companyname = formData.companyname;
        profileUpdateData.companyid = formData.companyid;
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${guestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(profileUpdateData),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.detail || 'Failed to update profile';
        toast.error(errorMsg);
        setIsSubmitting(false);
        return;
      }

      toast.success('Profile updated successfully!');
      await fetchGuestData();
      setIsEditModalOpen(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile');
      setIsSubmitting(false);
    }
  };

  // ==================== MAIN SUBMIT FUNCTION ====================

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Set submitting state to true
    setIsSubmitting(true);

    // Validate names
    const firstnameError = validateName(formData.firstname);
    const lastnameError = validateName(formData.lastname);

    setValidationErrors({
      firstname: firstnameError,
      lastname: lastnameError,
    });

    if (firstnameError || lastnameError) {
      toast.error(`${firstnameError || lastnameError}`);
      setIsSubmitting(false);
      return;
    }

    // Check ALL fields for changes
    const firstNameChanged = formData.firstname !== guestDetails?.firstname;
    const lastNameChanged = formData.lastname !== guestDetails?.lastname;
    const emailChanged = formData.emailid !== guestDetails?.emailid;
    const phoneChanged =
      cleanPhoneNumber(formData.phonenumber) !== cleanPhoneNumber(guestDetails?.phonenumber);
    const countryChanged = formData.country !== guestDetails?.country;
    const clientTypeChanged = formData.client_type !== guestDetails?.client_type;
    const companyNameChanged = formData.companyname !== guestDetails?.companyname;
    const companyIdChanged = formData.companyid !== guestDetails?.companyid;

    const anyChanges =
      firstNameChanged ||
      lastNameChanged ||
      emailChanged ||
      phoneChanged ||
      countryChanged ||
      clientTypeChanged ||
      companyNameChanged ||
      companyIdChanged;

    if (!anyChanges && !profileImage) {
      toast.info('No changes detected. Please update at least one field.');
      setIsSubmitting(false);
      return;
    }

    try {
      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        toast.error('Session expired. Please login again.');
        setIsSubmitting(false);
        return;
      }

      // If profile image was changed, update it along with everything else
      if (profileImage) {
        const formPayload = new FormData();
        formPayload.append('profile_photo', profileImage);
        formPayload.append('first_name', formData.firstname.trim());
        formPayload.append('last_name', formData.lastname.trim());
        formPayload.append('client_type', formData.client_type.toLowerCase());
        formPayload.append('country', selectedCountry.name);

        if (formData.emailid && formData.emailid.trim()) {
          formPayload.append('email', formData.emailid.trim().toLowerCase());
        }

        if (formData.phonenumber) {
          formPayload.append('country_code', formData.countrycode);
          formPayload.append('phone_number', formData.phonenumber);
        }

        if (formData.client_type === 'Corporate') {
          if (formData.companyid?.trim()) {
            formPayload.append('company_id', formData.companyid.trim());
          }
          if (formData.companyname?.trim()) {
            formPayload.append('company_name', formData.companyname.trim());
          }
        }

        const response = await fetch(`${BQ_BASE_URL}/bq/api/complete-profile`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formPayload,
        });

        const data = await response.json();

        if (!response.ok) {
          let errorMessage = 'Failed to update profile';
          if (data.detail) {
            errorMessage =
              typeof data.detail === 'string'
                ? data.detail
                : data.detail.map((err) => err.msg).join(', ');
          } else if (data.message) {
            errorMessage = data.message;
          }
          throw new Error(errorMessage);
        }

        toast.success('Profile updated successfully!');
        setProfileImage(null);
        await fetchGuestData();
        setIsEditModalOpen(false);
        setIsSubmitting(false);
        return;
      }

      // If email OR phone changed → Send OTP first
      if (emailChanged || phoneChanged) {
        const updateData = {
          email: formData.emailid || '',
          phone_number: String(formData.phonenumber),
          country_code: selectedCountry.dialCode || '+91',
        };

        await handleUpdateContact(updateData);
        setIsSubmitting(false);
        return;
      }

      // If ONLY name/country/client type changed → Direct update (no OTP)
      await updateProfileFields();
      setIsSubmitting(false);
    } catch (error) {
      console.error('Submit error:', error);
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        <p className="ml-4">Loading profile data...</p>
      </div>
    );
  }

  // No profile data
  if (!guestDetails) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4">
        <div className="bg-yellow-50 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative max-w-md">
          <p>No profile data found. Please check your authentication.</p>
          <button
            onClick={fetchGuestData}
            className="mt-2 bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white mt-16 p-6">
      <button
        onClick={() => navigate(-1)}
        className="absolute top-24 left-5 text-black hover:text-gray-700 transition font-semibold text-2xl"
      >
        <IoMdArrowRoundBack size={32} />
      </button>

      <div className="flex justify-start px-10">
        {[
          { id: 'guestDetails', label: 'Guest Details' },
          { id: 'bookingHistory', label: 'Reservation History' },
          { id: 'helpSupport', label: 'Help & Support' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`py-2 px-4 font-semibold ${
              activeTab === tab.id ? 'border-b-2 border-gray-500 text-black' : 'text-gray-400'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'guestDetails' && guestDetails && (
        <div className="px-4 sm:px-6 lg:px-8 py-3 w-full mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Guest Profile</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Review and manage guest information. All personal details are securely stored and
              protected
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Profile Card */}
            <div className="w-full lg:w-1/3">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="h-48 bg-gradient-to-r from-blue-50 to-gray-50 flex items-center justify-center">
                  {guestDetails.profilephoto ? (
                    <img
                      src={guestDetails.profilephoto}
                      alt="Profile"
                      className="object-cover w-40 h-40 rounded-full border-4 border-white shadow-md"
                      onError={(e) => {
                        e.target.src =
                          'https://cdn.pixabay.com/photo/2015/03/04/22/35/avatar-659652_640.png';
                      }}
                    />
                  ) : (
                    <div className="w-40 h-40 rounded-full border-4 border-white shadow-md bg-gray-200 flex items-center justify-center">
                      <FiUser className="w-20 h-20 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {guestDetails.firstname} {guestDetails.lastname}
                  </h3>
                  <p className="text-gray-500 mt-1">{guestDetails.client_type} Guest</p>
                </div>
              </div>

              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">Aadhar Card</h3>
                  <div className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <FiInfo className="w-3 h-3" />
                    <span>Verified Document</span>
                  </div>
                </div>
                {guestDetails.aadhar_file ? (
                  <div className="flex justify-center">
                    <img
                      src={guestDetails.aadhar_file}
                      alt="Aadhar Card"
                      className="max-w-full h-auto rounded border border-gray-200"
                      onError={(e) => {
                        e.target.src = '';
                        e.target.parentElement.innerHTML = `
                          <div class="text-center p-4 bg-gray-100 rounded-lg">
                            <svg class="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                            </svg>
                            <p class="mt-2 text-gray-500">Aadhar image could not be loaded</p>
                            <p class="text-xs text-gray-400 mt-1">Please contact front desk for assistance</p>
                          </div>
                        `;
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-center p-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <FiFileText className="w-12 h-12 mx-auto text-gray-400" />
                    <p className="mt-2 text-gray-500 font-medium">No Aadhar Card Uploaded</p>
                    <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-xs text-yellow-800">
                        <span className="font-semibold">Note for Guest:</span>
                        <br />
                        Aadhar card can only be uploaded by our front desk team.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Information Card */}
            <div className="w-full lg:w-2/3">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                  <button
                    onClick={openEditModal}
                    className="px-3 py-2 bg-black text-sm text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
                  >
                    <FiEdit2 className="w-4 h-4" /> Update Information
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      label: 'Full Name',
                      value: `${guestDetails.firstname.charAt(0).toUpperCase() + guestDetails.firstname.slice(1)} ${guestDetails.lastname.charAt(0).toUpperCase() + guestDetails.lastname.slice(1)}`,
                      icon: <FiUser className="w-5 h-5 text-gray-400" />,
                    },
                    {
                      label: 'Email Address',
                      value: guestDetails.emailid,
                      icon: <FiMail className="w-5 h-5 text-gray-400" />,
                    },
                    {
                      label: 'Phone Number',
                      value: guestDetails.phonenumber,
                      icon: <FiPhone className="w-5 h-5 text-gray-400" />,
                    },
                    {
                      label: 'Profile Type',
                      value: guestDetails.client_type,
                      icon: <FiUser className="w-5 h-5 text-gray-400" />,
                    },
                    ...(guestDetails.client_type === 'Corporate'
                      ? [
                          {
                            label: 'Company Name',
                            value: guestDetails.companyname || 'Not provided',
                            icon: <FiFileText className="w-5 h-5 text-gray-400" />,
                          },
                          {
                            label: 'Company ID',
                            value: guestDetails.companyid || 'Not provided',
                            icon: <FiFileText className="w-5 h-5 text-gray-400" />,
                          },
                        ]
                      : []),
                  ].map(({ label, value, icon }, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 bg-blue-100 p-2 rounded-lg">{icon}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-500">{label}</p>
                          <p
                            className="text-sm font-medium text-gray-900 mt-1 truncate"
                            title={value}
                          >
                            {value}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Edit Modal */}
          {isEditModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center border-b p-4 sticky top-0 bg-white z-10">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Update Profile Information
                  </h3>
                  <button onClick={closeEditModal} className="text-gray-500 hover:text-gray-700">
                    <FiX size={24} />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6">
                  <div className="space-y-4">
                    {/* Profile Photo Section - Shows current photo with update button */}
                    <div className="border-b border-gray-200 pb-4 mb-4">
                      <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <FiCamera className="text-blue-600" /> Profile Photo
                      </h4>

                      <div className="flex items-center gap-4">
                        {profilePreview ? (
                          <img
                            src={profilePreview}
                            alt="Profile"
                            className="w-40 h-30 rounded object-cover border-2 border-gray-300"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                            <FiUser className="w-10 h-10 text-gray-400" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={openPhotoPopup}
                          className="px-4 py-2 bg-black hover:bg-gray-900 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                          <FiCamera /> Update Photo
                        </button>
                      </div>
                      {profileImage && (
                        <p className="text-xs text-green-600 mt-2">✓ New photo selected</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name *
                        </label>
                        <input
                          type="text"
                          name="firstname"
                          value={formData.firstname}
                          onChange={handleInputChange}
                          className={`w-full p-2 border capitalize ${
                            validationErrors.firstname ? 'border-red-500' : 'border-gray-300'
                          } rounded `}
                          required
                        />
                        {validationErrors.firstname && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors.firstname}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          name="lastname"
                          value={formData.lastname}
                          onChange={handleInputChange}
                          className={`w-full p-2 border capitalize ${
                            validationErrors.lastname ? 'border-red-500' : 'border-gray-300'
                          } rounded `}
                          required
                        />
                        {validationErrors.lastname && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors.lastname}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          name="emailid"
                          value={formData.emailid}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded"
                        />
                        {formData.emailid !== guestDetails?.emailid && formData.emailid && (
                          <p className="text-xs text-blue-600 mt-1">
                            Verification OTP will be sent to this email
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phonenumber"
                          value={formData.phonenumber}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded"
                          required
                        />
                        {cleanPhoneNumber(formData.phonenumber) !==
                          cleanPhoneNumber(guestDetails?.phonenumber) &&
                          formData.phonenumber && (
                            <p className="text-xs text-blue-600 mt-1">
                              Verification OTP will be sent to this number
                            </p>
                          )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Country
                        </label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setIsCountryOpen(!isCountryOpen)}
                            className="w-full flex items-center justify-between p-2 border border-gray-300 rounded-md text-sm bg-white"
                          >
                            <span className="flex items-center gap-2">
                              <img
                                src={selectedCountry.flag}
                                alt={selectedCountry.name}
                                className="w-5 h-4 object-cover rounded-sm"
                              />
                              {selectedCountry.name}
                            </span>
                            <span className="ml-2">▼</span>
                          </button>

                          {isCountryOpen && (
                            <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto z-10">
                              {countries.map((country) => (
                                <div
                                  key={country.code}
                                  onClick={() => {
                                    setSelectedCountry(country);
                                    setIsCountryOpen(false);
                                    setFormData((prev) => ({
                                      ...prev,
                                      countrycode: country.dialCode,
                                      country: country.name,
                                    }));
                                  }}
                                  className="flex items-center gap-2 p-2 hover:bg-gray-100 cursor-pointer"
                                >
                                  <img
                                    src={country.flag}
                                    alt={country.name}
                                    className="w-5 h-4 object-cover rounded-sm"
                                  />
                                  {country.name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Profile Type
                        </label>
                        <select
                          name="client_type"
                          value={formData.client_type}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded"
                          required
                        >
                          <option value="Leisure">Leisure</option>
                          <option value="Corporate">Corporate</option>
                        </select>
                      </div>
                      {formData.client_type === 'Corporate' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Company Name *
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                name="companyname"
                                placeholder="Search company name..."
                                value={formData.companyname}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setFormData((prev) => ({
                                    ...prev,
                                    companyname: value,
                                    companyid: '',
                                  }));
                                  setCompanySearchTerm(value);
                                }}
                                onBlur={() => {
                                  setTimeout(() => setIsCompanyDropdownOpen(false), 200);
                                }}
                                onFocus={() => {
                                  if (companySearchTerm && companySearchTerm.length > 0) {
                                    setIsCompanyDropdownOpen(true);
                                  }
                                }}
                                className="w-full p-2 border border-gray-300 rounded"
                                required
                              />

                              {isCompanyDropdownOpen && filteredCompanies.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                                  {filteredCompanies.map((company) => (
                                    <div
                                      key={company.id}
                                      onClick={() => {
                                        setFormData((prev) => ({
                                          ...prev,
                                          companyid:
                                            company.companyid || company.id?.toString() || '',
                                          companyname: company.companyname,
                                        }));
                                        setCompanySearchTerm(company.companyname);
                                        setIsCompanyDropdownOpen(false);
                                        toast.success(`Selected: ${company.companyname}`);
                                      }}
                                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
                                    >
                                      <div className="font-medium text-sm">
                                        {company.companyname}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        ID: {company.companyid || company.id}
                                        {company.gstnumber && ` | GST: ${company.gstnumber}`}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {isCompanyDropdownOpen &&
                                companySearchTerm.length > 2 &&
                                filteredCompanies.length === 0 &&
                                allCompanies.length > 0 && (
                                  <div className="absolute z-50 w-full mt-1 bg-yellow-50 border border-yellow-300 rounded-md shadow-lg p-3">
                                    <div className="flex items-start gap-2">
                                      <svg
                                        className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      <div>
                                        <p className="text-sm text-yellow-800 font-medium">
                                          Company not found
                                        </p>
                                        <p className="text-xs text-yellow-700 mt-1">
                                          ℹ️ If your company is not in the list, please contact the
                                          front desk to register your company for future use.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                            </div>
                            {formData.companyid && (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ Company ID: {formData.companyid}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Company ID *
                            </label>
                            <input
                              type="text"
                              name="companyid"
                              value={formData.companyid}
                              onChange={handleInputChange}
                              className={`w-full p-2 border border-gray-300 rounded ${formData.companyid ? 'bg-gray-50' : ''}`}
                              placeholder="Enter company ID"
                              required
                              readOnly={!!formData.companyid}
                              title={formData.companyid ? 'Auto-filled from company selection' : ''}
                            />
                            {formData.companyid && (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ Company ID auto-filled
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-start gap-2">
                        <FiInfo className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">Important Note</p>
                          <p className="text-xs text-blue-600 mt-1">
                            Aadhar card updates can only be done by our front desk team. Please
                            visit the reception for any changes.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={closeEditModal}
                      className="px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-black text-white font-semibold rounded-md hover:bg-gray-800 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        isSubmitting || validationErrors.firstname || validationErrors.lastname
                      }
                    >
                      {isSubmitting ? (
                        <>
                          <svg
                            className="animate-spin h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiCheck /> Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Photo Capture Popup */}
          {isPhotoPopupOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
                <div className="flex justify-between items-center border-b p-4">
                  <div className="mt-2">
                    <h3 className="text-lg font-semibold text-gray-900">Take Profile Photo</h3>
                    <p className="text-xs text-red-500 mt-1">
                      Ensure good lighting and you're alone in the picture. This will help you with
                      fast check-in via face recognition.
                    </p>
                  </div>
                  <button onClick={closePhotoPopup} className="text-gray-500 hover:text-gray-700">
                    <FiX size={24} />
                  </button>
                </div>
                <div className="p-4">
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    className="rounded-lg w-full h-96 object-cover border-2 border-gray-300"
                    videoConstraints={{
                      facingMode: 'user',
                    }}
                  />

                  <div className="flex flex-wrap gap-3 justify-center mt-4">
                    <button
                      type="button"
                      onClick={captureImage}
                      className="flex items-center gap-2 px-6 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition"
                    >
                      <FiCamera /> Capture photo
                    </button>
                    <input
                      type="file"
                      id="photo-upload-popup"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="photo-upload-popup"
                      className="flex items-center gap-2 px-6 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition cursor-pointer"
                    >
                      <FiCamera /> Upload photo
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* OTP Verification Modal */}
          {showOtpModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-md sm:max-w-lg overflow-hidden relative">
                <button
                  onClick={() => {
                    setShowOtpModal(false);
                    setOtp('');
                    setOtpError('');
                    rollbackContactChanges();
                    setPendingChanges(null);
                  }}
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>

                <div className="p-6 sm:p-8">
                  <div className="text-center">
                    <div className="mx-auto flex items-center justify-center h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-blue-200 mb-4">
                      {otpType === 'email' ? (
                        <FiMail className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600" />
                      ) : (
                        <FiPhone className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600" />
                      )}
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                      Verify Your {otpType === 'email' ? 'Email' : 'Phone Number'}
                    </h2>
                    <p className="text-gray-600 mb-6 text-sm sm:text-base">
                      We've sent a 6-digit verification code to{' '}
                      <span className="font-semibold text-black">{otpIdentifier}</span>
                    </p>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                      Enter verification code
                    </label>
                    <div className="flex justify-center space-x-2 sm:space-x-3">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                          key={index}
                          type="text"
                          maxLength="1"
                          value={otp[index] || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value === e.target.value) {
                              const newOtp = otp.split('');
                              newOtp[index] = value;
                              setOtp(newOtp.join(''));
                              if (value && index < 5) {
                                document.getElementById(`otp-input-${index + 1}`)?.focus();
                              }
                              if (otpError) setOtpError('');
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Backspace' && !otp[index] && index > 0) {
                              document.getElementById(`otp-input-${index - 1}`)?.focus();
                            }
                          }}
                          id={`otp-input-${index}`}
                          className={`w-10 h-10 sm:w-12 sm:h-12 text-xl sm:text-2xl text-center border ${
                            otpError ? 'border-red-500' : 'border-gray-300'
                          } rounded-md focus:outline-none focus:ring-2 focus:ring-black`}
                          inputMode="numeric"
                          pattern="[0-9]*"
                        />
                      ))}
                    </div>
                    {otpError && (
                      <p className="mt-2 text-sm text-red-600 text-center">{otpError}</p>
                    )}
                  </div>

                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={handleVerifyOtp}
                      className="w-full py-2.5 sm:py-3 px-4 bg-black hover:text-gray-300 text-white font-medium rounded-lg shadow transition-colors disabled:opacity-50"
                      disabled={isVerifying || otp.length !== 6}
                    >
                      {isVerifying ? (
                        <span className="flex items-center justify-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Verifying...
                        </span>
                      ) : (
                        'Verify and Update'
                      )}
                    </button>
                  </div>
                  <div className="mt-6 text-center text-xs sm:text-sm text-gray-500">
                    Didn't receive code?{' '}
                    {canResend ? (
                      <button
                        className="font-medium text-blue-600 hover:text-blue-500"
                        onClick={handleResendOtp}
                        disabled={isVerifying}
                      >
                        Resend OTP
                      </button>
                    ) : (
                      <span className="text-gray-500">Resend OTP in {resendTimer} seconds</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'bookingHistory' && <BookingHistory bookings={bookings} />}
      {activeTab === 'helpSupport' && <HelpSupportForm guestDetails={guestDetails} />}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Profile;

// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { IoMdArrowRoundBack } from 'react-icons/io';
// import { FiEdit2, FiX, FiCheck, FiUser, FiFileText, FiMail, FiPhone, FiInfo } from 'react-icons/fi';
// import BookingHistory from './BookingHistory';
// import Cookies from 'js-cookie';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import HelpSupportForm from './HelpSupportForm';
// import countries from '../CHECK-IN/Walk-In/countries';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const Profile = () => {
//   const [activeTab, setActiveTab] = useState('guestDetails');
//   const [guestDetails, setGuestDetails] = useState(null);
//   const [bookings, setBookings] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [isEditModalOpen, setIsEditModalOpen] = useState(false);
//   const [formData, setFormData] = useState({
//     firstname: '',
//     lastname: '',
//     emailid: '',
//     phonenumber: '',
//     client_type: 'Leisure',
//     companyname: '',
//     employeeid: '',
//     companyid: '',
//     country: '',
//     countrycode: '+91',
//   });
//   const [validationErrors, setValidationErrors] = useState({
//     firstname: null,
//     lastname: null,
//   });

//   // OTP Verification States
//   const [showOtpModal, setShowOtpModal] = useState(false);
//   const [otp, setOtp] = useState('');
//   const [otpError, setOtpError] = useState('');
//   const [otpIdentifier, setOtpIdentifier] = useState('');
//   const [otpType, setOtpType] = useState('email');
//   const [resendTimer, setResendTimer] = useState(300);
//   const [canResend, setCanResend] = useState(false);
//   const [isVerifying, setIsVerifying] = useState(false);
//   const [pendingChanges, setPendingChanges] = useState(null); // Store pending changes for rollback
//   const [originalContactData, setOriginalContactData] = useState(null); // Store original data for rollback

//   // Company search state
//   const [companySearchTerm, setCompanySearchTerm] = useState('');
//   const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
//   const [filteredCompanies, setFilteredCompanies] = useState([]);
//   const [allCompanies, setAllCompanies] = useState([]);

//   // Country dropdown state
//   const [selectedCountry, setSelectedCountry] = useState(
//     countries.find((country) => country.name === 'India') || countries[0]
//   );
//   const [isCountryOpen, setIsCountryOpen] = useState(false);

//   const navigate = useNavigate();

//   // Fetch companies for autocomplete
//   useEffect(() => {
//     const fetchCompanies = async () => {
//       try {
//         const accessToken = Cookies.get('access_token');
//         if (!accessToken) return;

//         const response = await fetch(`${BQ_BASE_URL}/bq/api/companies`, {
//           method: 'GET',
//           headers: {
//             Accept: 'application/json',
//             Authorization: `Bearer ${accessToken}`,
//           },
//         });

//         if (!response.ok) {
//           throw new Error('Failed to fetch companies');
//         }

//         const data = await response.json();
//         if (data && data.companies) {
//           setAllCompanies(data.companies);
//           setFilteredCompanies(data.companies);
//         }
//       } catch (error) {
//         console.error('Error fetching companies:', error);
//       }
//     };

//     fetchCompanies();
//   }, []);

//   // Filter companies based on search term
//   useEffect(() => {
//     if (companySearchTerm && companySearchTerm.length > 0) {
//       const searchLower = companySearchTerm.toLowerCase();
//       const filtered = allCompanies.filter(
//         (company) =>
//           company.companyname.toLowerCase().includes(searchLower) ||
//           company.companyid?.toLowerCase().includes(searchLower)
//       );
//       setFilteredCompanies(filtered);
//       setIsCompanyDropdownOpen(true);
//     } else {
//       setFilteredCompanies([]);
//       setIsCompanyDropdownOpen(false);
//     }
//   }, [companySearchTerm, allCompanies]);

//   // Filter companies based on search term
//   useEffect(() => {
//     if (companySearchTerm && companySearchTerm.length > 0) {
//       const searchLower = companySearchTerm.toLowerCase();
//       const filtered = allCompanies.filter(
//         (company) =>
//           company.companyname.toLowerCase().includes(searchLower) ||
//           company.companyid?.toLowerCase().includes(searchLower)
//       );
//       setFilteredCompanies(filtered);
//       setIsCompanyDropdownOpen(true);
//     } else {
//       setFilteredCompanies([]);
//       setIsCompanyDropdownOpen(false);
//     }
//   }, [companySearchTerm, allCompanies]);

//   // Timer for OTP resend
//   useEffect(() => {
//     if (showOtpModal && !canResend) {
//       const timer = setInterval(() => {
//         setResendTimer((prev) => {
//           if (prev <= 1) {
//             clearInterval(timer);
//             setCanResend(true);
//             return 0;
//           }
//           return prev - 1;
//         });
//       }, 1000);

//       return () => clearInterval(timer);
//     }
//   }, [showOtpModal, canResend]);

//   // Helper function to clean phone numbers
//   const cleanPhoneNumber = (phone) => {
//     if (!phone) return '';
//     return phone.toString().replace(/\D/g, '');
//   };

//   const decryptUserData = async (encryptedData) => {
//     try {
//       const decryptedData = { ...encryptedData };
//       const fieldsToDecrypt = {};

//       if (encryptedData.email && encryptedData.email.length > 50) {
//         fieldsToDecrypt.email = encryptedData.email;
//       } else if (encryptedData.email) {
//         decryptedData.email = encryptedData.email;
//       }

//       const phoneStr = String(encryptedData.phone_number || '');
//       if (phoneStr && !/^[\d\+\-\s\(\)]+$/.test(phoneStr)) {
//         fieldsToDecrypt.phone_number = encryptedData.phone_number;
//       } else if (phoneStr) {
//         decryptedData.phone_number = encryptedData.phone_number;
//       }

//       if (encryptedData.country_code && encryptedData.country_code.length > 10) {
//         fieldsToDecrypt.country_code = encryptedData.country_code;
//       } else if (encryptedData.country_code) {
//         decryptedData.country_code = encryptedData.country_code;
//       }

//       if (Object.keys(fieldsToDecrypt).length === 0) {
//         return decryptedData;
//       }

//       const accessToken = Cookies.get('access_token');
//       if (!accessToken) {
//         throw new Error('No access token found');
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify({
//           encrypted_fields: fieldsToDecrypt,
//         }),
//       });

//       if (response.status === 401) {
//         return decryptedData;
//       }

//       if (!response.ok) {
//         throw new Error(`Decryption failed: ${response.statusText}`);
//       }

//       const decryptedResult = await response.json();
//       return {
//         ...decryptedData,
//         ...decryptedResult,
//       };
//     } catch (error) {
//       console.error('Decryption error:', error);
//       return encryptedData;
//     }
//   };

//   const fetchGuestData = async () => {
//     setIsLoading(true);

//     try {
//       const accessToken = Cookies.get('access_token');
//       if (!accessToken) {
//         toast.error('Session expired. Please login again.');
//         setIsLoading(false);
//         return;
//       }

//       const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
//       if (!guestId) {
//         toast.error('No guest ID found. Please login again.');
//         setIsLoading(false);
//         return;
//       }

//       const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/guests/${guestId}`, {
//         method: 'GET',
//         headers: {
//           Accept: 'application/json',
//           Authorization: `Bearer ${accessToken}`,
//         },
//       });

//       if (!profileResponse.ok) {
//         throw new Error(`Profile API Error: ${profileResponse.statusText}`);
//       }

//       const profileData = await profileResponse.json();
//       const guest = profileData.guest;

//       const isDecrypted = guest.emailid && guest.emailid.length < 50;

//       let guestData;
//       if (isDecrypted) {
//         guestData = {
//           guest_id: guestId,
//           firstname: guest.firstname || '',
//           lastname: guest.lastname || '',
//           emailid: guest.emailid || '',
//           phonenumber: guest.phonenumber || '',
//           countrycode: guest.countrycode || '+91',
//           client_type: guest.clienttype || 'Leisure',
//           profilephoto: guest.profile_photo || null,
//           aadhar_file: guest.aadhar_image_url || null,
//           companyname: guest.companyname || '',
//           employeeid: guest.employeeid || '',
//           companyid: guest.companyid || '',
//           country: guest.country || 'India',
//         };
//       } else {
//         const decryptedFields = await decryptUserData({
//           email: guest.emailid,
//           phone_number: guest.phonenumber,
//           country_code: guest.countrycode,
//         });

//         guestData = {
//           guest_id: guestId,
//           firstname: guest.firstname || '',
//           lastname: guest.lastname || '',
//           emailid: decryptedFields.email || guest.emailid || '',
//           phonenumber: decryptedFields.phone_number || guest.phonenumber || '',
//           countrycode: decryptedFields.country_code || guest.countrycode || '+91',
//           client_type: guest.clienttype || 'Leisure',
//           profilephoto: guest.profile_photo || null,
//           aadhar_file: guest.aadhar_image_url || null,
//           companyname: guest.companyname || '',
//           employeeid: guest.employeeid || '',
//           companyid: guest.companyid || '',
//           country: guest.country || 'India',
//         };
//       }

//       setGuestDetails(guestData);
//       setFormData({
//         firstname: guestData.firstname,
//         lastname: guestData.lastname,
//         emailid: guestData.emailid,
//         phonenumber: guestData.phonenumber,
//         client_type: guestData.client_type,
//         companyname: guestData.companyname,
//         employeeid: guestData.employeeid,
//         companyid: guestData.companyid,
//         country: guestData.country,
//         countrycode: guestData.countrycode,
//       });

//       // Set selected country using dialCode
//       let guestCountry = countries.find(
//         (country) =>
//           country.name === guestData.country ||
//           country.dialCode === (guestData.countrycode || '+91')
//       );
//       if (!guestCountry) {
//         guestCountry = countries.find((country) => country.dialCode === '+91') || countries[0];
//       }
//       setSelectedCountry(guestCountry);

//       // Fetch bookings
//       const bookingsResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/profile/bookings/?guestid=${guestId}`,
//         {
//           method: 'GET',
//           headers: {
//             Accept: 'application/json',
//             Authorization: `Bearer ${accessToken}`,
//           },
//         }
//       );

//       if (bookingsResponse.ok) {
//         const bookingsData = await bookingsResponse.json();
//         setBookings(bookingsData.bookings || []);
//       }
//     } catch (err) {
//       console.error('Error in fetchGuestData:', err);
//       toast.error(`Failed to load profile: ${err.message}`);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const getGuestIdFromToken = (token) => {
//     try {
//       const payload = JSON.parse(atob(token.split('.')[1]));
//       return payload.sub;
//     } catch (e) {
//       console.error('Error parsing token:', e);
//       return null;
//     }
//   };

//   useEffect(() => {
//     fetchGuestData();
//   }, []);

//   const validateName = (name) => {
//     if (!name.trim()) {
//       return 'This field is required';
//     }
//     if (name.trim().length < 2) {
//       return 'Must be at least 2 characters long';
//     }
//     if (/\d/.test(name)) {
//       return 'Should not contain numbers';
//     }
//     return null;
//   };

//   const openEditModal = () => {
//     if (guestDetails) {
//       setFormData({
//         firstname: guestDetails.firstname || '',
//         lastname: guestDetails.lastname || '',
//         emailid: guestDetails.emailid || '',
//         phonenumber: guestDetails.phonenumber || '',
//         client_type: guestDetails.client_type || 'Leisure',
//         companyname: guestDetails.companyname || '',
//         employeeid: guestDetails.employeeid || '',
//         companyid: guestDetails.companyid || '',
//         country: guestDetails.country || 'India',
//         countrycode: guestDetails.countrycode || '+91',
//       });

//       let guestCountry = countries.find(
//         (country) =>
//           country.name === guestDetails.country ||
//           country.dialCode === (guestDetails.countrycode || '+91')
//       );
//       if (!guestCountry) {
//         guestCountry = countries.find((country) => country.dialCode === '+91') || countries[0];
//       }
//       setSelectedCountry(guestCountry);
//     }
//     setIsEditModalOpen(true);
//   };

//   const closeEditModal = () => {
//     setIsEditModalOpen(false);
//     setOtpError('');
//     setPendingChanges(null);
//     setOriginalContactData(null);
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;

//     if (name === 'firstname' || name === 'lastname') {
//       const error = validateName(value);
//       setValidationErrors((prev) => ({
//         ...prev,
//         [name]: error,
//       }));
//     }

//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   // ==================== OTP FUNCTIONS ====================

//   // Update contact and send OTP - Store original data for rollback
//   const handleUpdateContact = async (updateData) => {
//     try {
//       const accessToken = Cookies.get('access_token');
//       if (!accessToken) {
//         toast.error('Session expired. Please login again.');
//         return;
//       }

//       // Store original data for rollback in case OTP fails
//       setOriginalContactData({
//         email: guestDetails?.emailid || '',
//         phone: guestDetails?.phonenumber || '',
//         country_code: guestDetails?.countrycode || '+91',
//       });

//       const cleanedData = {
//         email: updateData.email || '',
//         phone_number: updateData.phone_number ? String(updateData.phone_number) : undefined,
//         country_code: updateData.country_code || '+91',
//       };

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/update-contact`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify(cleanedData),
//       });

//       const data = await response.json();

//       if (!response.ok) {
//         const errorMessage = data.detail || 'Failed to update contact';

//         if (typeof errorMessage === 'string') {
//           if (errorMessage.includes('Email already registered')) {
//             toast.error('This email is already registered with another account.');
//           } else if (errorMessage.includes('Phone number already registered')) {
//             toast.error('This phone number is already registered with another account.');
//           } else {
//             toast.error(errorMessage);
//           }
//         } else {
//           toast.error('Failed to update profile. Please try again.');
//         }

//         throw new Error(typeof errorMessage === 'string' ? errorMessage : 'Update failed');
//       }

//       const results = data.results || {};

//       // Check what was actually changed
//       const emailChanged = formData.emailid !== guestDetails?.emailid;
//       const phoneChanged =
//         cleanPhoneNumber(formData.phonenumber) !== cleanPhoneNumber(guestDetails?.phonenumber);

//       const phoneResult = results.phone_number;
//       const emailResult = results.email;

//       // Store which field was changed for OTP verification
//       const changes = {
//         emailChanged: emailChanged && updateData.email,
//         phoneChanged: phoneChanged && updateData.phone_number,
//         newEmail: updateData.email,
//         newPhone: updateData.phone_number,
//       };
//       setPendingChanges(changes);

//       // If phone was changed and verification is required, show phone OTP
//       if (phoneChanged && phoneResult?.verification_required) {
//         setOtpIdentifier(phoneResult.identifier || updateData.phone_number);
//         setOtpType('phone');
//         setShowOtpModal(true);
//         setResendTimer(300);
//         setCanResend(false);
//         toast.info('Verification code sent to your new phone number');
//         return data;
//       }

//       // If email was changed (and has value) and verification is required, show email OTP
//       if (emailChanged && updateData.email && emailResult?.verification_required) {
//         setOtpIdentifier(emailResult.identifier || updateData.email);
//         setOtpType('email');
//         setShowOtpModal(true);
//         setResendTimer(300);
//         setCanResend(false);
//         toast.info('Verification code sent to your new email');
//         return data;
//       }

//       // If no verification required, update was successful
//       toast.success('Profile updated successfully!');
//       await fetchGuestData();
//       setIsEditModalOpen(false);
//       return data;
//     } catch (error) {
//       console.error('Error updating contact:', error);
//       throw error;
//     }
//   };

//   // Rollback contact changes if OTP verification fails
//   const rollbackContactChanges = async () => {
//     if (!originalContactData) return;

//     try {
//       const accessToken = Cookies.get('access_token');
//       if (!accessToken) return;

//       // Rollback to original values
//       await fetch(`${BQ_BASE_URL}/bq/api/update-contact`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify({
//           email: originalContactData.email || '',
//           phone_number: originalContactData.phone ? String(originalContactData.phone) : undefined,
//           country_code: originalContactData.country_code || '+91',
//         }),
//       });

//       console.log('Contact rolled back to original values');
//     } catch (error) {
//       console.error('Error rolling back contact:', error);
//     } finally {
//       setOriginalContactData(null);
//     }
//   };

//   // Verify OTP
//   const handleVerifyOtp = async () => {
//     if (otp.length !== 6) {
//       setOtpError('Please enter 6-digit OTP');
//       return;
//     }

//     setIsVerifying(true);

//     try {
//       const accessToken = Cookies.get('access_token');
//       if (!accessToken) {
//         toast.error('Session expired. Please login again.');
//         setIsVerifying(false);
//         return;
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/verify-email-phone-otp`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify({
//           identifier: otpIdentifier,
//           otp: otp,
//         }),
//       });

//       const data = await response.json();

//       if (!response.ok) {
//         const errorMessage = data.detail || 'Verification failed';

//         // ❌ OTP verification FAILED - Rollback the contact changes
//         await rollbackContactChanges();

//         if (errorMessage.includes('Invalid OTP')) {
//           toast.error('Invalid OTP. Please check and try again.');
//         } else if (errorMessage.includes('expired')) {
//           toast.error('OTP has expired. Please request a new one.');
//         } else {
//           toast.error(errorMessage);
//         }
//         throw new Error(errorMessage);
//       }

//       // ✅ OTP verification SUCCESS - Keep the changes
//       toast.success(`${otpType === 'email' ? 'Email' : 'Phone number'} verified successfully!`);

//       setShowOtpModal(false);
//       setOtp('');
//       setOtpError('');
//       setPendingChanges(null);
//       setOriginalContactData(null);

//       // Update the rest of the profile (name, country, client type)
//       await updateProfileFields();

//       // Refresh guest data
//       await fetchGuestData();
//       setIsEditModalOpen(false);
//     } catch (error) {
//       console.error('OTP verification error:', error);
//       setOtpError(error.message);
//     } finally {
//       setIsVerifying(false);
//     }
//   };

//   // Resend OTP
//   const handleResendOtp = async () => {
//     try {
//       const accessToken = Cookies.get('access_token');
//       if (!accessToken) {
//         toast.error('Session expired. Please login again.');
//         return;
//       }

//       const resendData = {
//         email: formData.emailid,
//         phone_number: String(formData.phonenumber),
//         country_code: selectedCountry.dialCode || '+91',
//       };

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/update-contact`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify(resendData),
//       });

//       const data = await response.json();

//       if (!response.ok) {
//         throw new Error(data.detail || 'Failed to resend OTP');
//       }

//       setOtp('');
//       setOtpError('');
//       toast.success('New OTP sent successfully');
//       setResendTimer(300);
//       setCanResend(false);
//     } catch (error) {
//       console.error('Error resending OTP:', error);
//       toast.error(`${error.message || 'Failed to resend OTP'}`);
//     }
//   };

//   // Update profile fields (name, country, client type) - NO OTP
//   const updateProfileFields = async () => {
//     try {
//       const accessToken = Cookies.get('access_token');
//       if (!accessToken) {
//         toast.error('Session expired. Please login again.');
//         return;
//       }

//       const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);

//       const profileUpdateData = {
//         firstname: formData.firstname,
//         lastname: formData.lastname,
//         country: selectedCountry.name || 'India',
//         countrycode: selectedCountry.dialCode || '+91',
//         clienttype: formData.client_type,
//       };

//       if (formData.client_type === 'Corporate') {
//         profileUpdateData.companyname = formData.companyname;
//         profileUpdateData.companyid = formData.companyid;
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/guests/${guestId}`, {
//         method: 'PUT',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify(profileUpdateData),
//       });

//       const data = await response.json();

//       if (!response.ok) {
//         const errorMsg = data.detail || 'Failed to update profile';
//         toast.error(errorMsg);
//         return;
//       }

//       toast.success('Profile updated successfully!');
//       await fetchGuestData();
//       setIsEditModalOpen(false);
//     } catch (error) {
//       console.error('Error updating profile:', error);
//       toast.error(error.message || 'Failed to update profile');
//     }
//   };

//   // ==================== MAIN SUBMIT FUNCTION ====================

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     // Validate names
//     const firstnameError = validateName(formData.firstname);
//     const lastnameError = validateName(formData.lastname);

//     setValidationErrors({
//       firstname: firstnameError,
//       lastname: lastnameError,
//     });

//     if (firstnameError || lastnameError) {
//       toast.error(`${firstnameError || lastnameError}`);
//       return;
//     }

//     // Check ALL fields for changes
//     const firstNameChanged = formData.firstname !== guestDetails?.firstname;
//     const lastNameChanged = formData.lastname !== guestDetails?.lastname;
//     const emailChanged = formData.emailid !== guestDetails?.emailid;
//     const phoneChanged =
//       cleanPhoneNumber(formData.phonenumber) !== cleanPhoneNumber(guestDetails?.phonenumber);
//     const countryChanged = formData.country !== guestDetails?.country;
//     const clientTypeChanged = formData.client_type !== guestDetails?.client_type;
//     const companyNameChanged = formData.companyname !== guestDetails?.companyname;
//     const companyIdChanged = formData.companyid !== guestDetails?.companyid;

//     const anyChanges =
//       firstNameChanged ||
//       lastNameChanged ||
//       emailChanged ||
//       phoneChanged ||
//       countryChanged ||
//       clientTypeChanged ||
//       companyNameChanged ||
//       companyIdChanged;

//     if (!anyChanges) {
//       toast.info('No changes detected. Please update at least one field.');
//       return;
//     }

//     try {
//       const accessToken = Cookies.get('access_token');
//       if (!accessToken) {
//         toast.error('Session expired. Please login again.');
//         return;
//       }

//       // ✅ If email OR phone changed → Send OTP first (UPDATE happens in API call)
//       if (emailChanged || phoneChanged) {
//         const updateData = {
//           email: formData.emailid || '',
//           phone_number: String(formData.phonenumber),
//           country_code: selectedCountry.dialCode || '+91',
//         };

//         await handleUpdateContact(updateData);
//         return;
//       }

//       // ✅ If ONLY name/country/client type changed → Direct update (no OTP)
//       await updateProfileFields();
//     } catch (error) {
//       console.error('Submit error:', error);
//       // Don't show toast here if it was already shown in handleUpdateContact
//     }
//   };

//   // Loading state
//   if (isLoading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
//         <p className="ml-4">Loading profile data...</p>
//       </div>
//     );
//   }

//   // No profile data
//   if (!guestDetails) {
//     return (
//       <div className="flex flex-col items-center justify-center h-screen p-4">
//         <div className="bg-yellow-50 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative max-w-md">
//           <p>No profile data found. Please check your authentication.</p>
//           <button
//             onClick={fetchGuestData}
//             className="mt-2 bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded"
//           >
//             Retry
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="bg-white mt-16 p-6">
//       <button
//         onClick={() => navigate(-1)}
//         className="absolute top-24 left-5 text-black hover:text-gray-700 transition font-semibold text-2xl"
//       >
//         <IoMdArrowRoundBack size={32} />
//       </button>

//       <div className="flex justify-start px-10">
//         {[
//           { id: 'guestDetails', label: 'Guest Details' },
//           { id: 'bookingHistory', label: 'Reservation History' },
//           { id: 'helpSupport', label: 'Help & Support' },
//         ].map((tab) => (
//           <button
//             key={tab.id}
//             onClick={() => setActiveTab(tab.id)}
//             className={`py-2 px-4 font-semibold ${
//               activeTab === tab.id ? 'border-b-2 border-gray-500 text-black' : 'text-gray-400'
//             }`}
//           >
//             {tab.label}
//           </button>
//         ))}
//       </div>

//       {activeTab === 'guestDetails' && guestDetails && (
//         <div className="px-4 sm:px-6 lg:px-8 py-3 w-full mx-auto">
//           <div className="text-center mb-6">
//             <h2 className="text-2xl font-bold text-gray-900">Guest Profile</h2>
//             <p className="text-gray-500 max-w-2xl mx-auto">
//               Review and manage guest information. All personal details are securely stored and
//               protected
//             </p>
//           </div>

//           <div className="flex flex-col lg:flex-row gap-8">
//             {/* Profile Card */}
//             <div className="w-full lg:w-1/3">
//               <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
//                 <div className="h-48 bg-gradient-to-r from-blue-50 to-gray-50 flex items-center justify-center">
//                   {guestDetails.profilephoto ? (
//                     <img
//                       src={guestDetails.profilephoto}
//                       alt="Profile"
//                       className="object-cover w-40 h-40 rounded-full border-4 border-white shadow-md"
//                       onError={(e) => {
//                         e.target.src =
//                           'https://cdn.pixabay.com/photo/2015/03/04/22/35/avatar-659652_640.png';
//                       }}
//                     />
//                   ) : (
//                     <div className="w-40 h-40 rounded-full border-4 border-white shadow-md bg-gray-200 flex items-center justify-center">
//                       <FiUser className="w-20 h-20 text-gray-400" />
//                     </div>
//                   )}
//                 </div>
//                 <div className="p-6 text-center">
//                   <h3 className="text-xl font-semibold text-gray-900">
//                     {guestDetails.firstname} {guestDetails.lastname}
//                   </h3>
//                   <p className="text-gray-500 mt-1">{guestDetails.client_type} Guest</p>
//                 </div>
//               </div>

//               <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
//                 <div className="flex items-center justify-between mb-3">
//                   <h3 className="text-lg font-semibold text-gray-900">Aadhar Card</h3>
//                   <div className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
//                     <FiInfo className="w-3 h-3" />
//                     <span>Verified Document</span>
//                   </div>
//                 </div>
//                 {guestDetails.aadhar_file ? (
//                   <div className="flex justify-center">
//                     <img
//                       src={guestDetails.aadhar_file}
//                       alt="Aadhar Card"
//                       className="max-w-full h-auto rounded border border-gray-200"
//                       onError={(e) => {
//                         e.target.src = '';
//                         e.target.parentElement.innerHTML = `
//                           <div class="text-center p-4 bg-gray-100 rounded-lg">
//                             <svg class="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
//                             </svg>
//                             <p class="mt-2 text-gray-500">Aadhar image could not be loaded</p>
//                             <p class="text-xs text-gray-400 mt-1">Please contact front desk for assistance</p>
//                           </div>
//                         `;
//                       }}
//                     />
//                   </div>
//                 ) : (
//                   <div className="text-center p-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
//                     <FiFileText className="w-12 h-12 mx-auto text-gray-400" />
//                     <p className="mt-2 text-gray-500 font-medium">No Aadhar Card Uploaded</p>
//                     <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
//                       <p className="text-xs text-yellow-800">
//                         <span className="font-semibold">Note for Guest:</span>
//                         <br />
//                         Aadhar card can only be uploaded by our front desk team.
//                       </p>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>

//             {/* Information Card */}
//             <div className="w-full lg:w-2/3">
//               <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
//                 <div className="flex items-center justify-between mb-6">
//                   <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
//                   <button
//                     onClick={openEditModal}
//                     className="px-3 py-2 bg-black text-sm text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
//                   >
//                     <FiEdit2 className="w-4 h-4" /> Update Information
//                   </button>
//                 </div>

//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   {[
//                     {
//                       label: 'Full Name',
//                       value: `${guestDetails.firstname.charAt(0).toUpperCase() + guestDetails.firstname.slice(1)} ${guestDetails.lastname.charAt(0).toUpperCase() + guestDetails.lastname.slice(1)}`,
//                       icon: <FiUser className="w-5 h-5 text-gray-400" />,
//                     },
//                     {
//                       label: 'Email Address',
//                       value: guestDetails.emailid,
//                       icon: <FiMail className="w-5 h-5 text-gray-400" />,
//                     },
//                     {
//                       label: 'Phone Number',
//                       value: guestDetails.phonenumber,
//                       icon: <FiPhone className="w-5 h-5 text-gray-400" />,
//                     },
//                     {
//                       label: 'Profile Type',
//                       value: guestDetails.client_type,
//                       icon: <FiUser className="w-5 h-5 text-gray-400" />,
//                     },
//                     ...(guestDetails.client_type === 'Corporate'
//                       ? [
//                           {
//                             label: 'Company Name',
//                             value: guestDetails.companyname || 'Not provided',
//                             icon: <FiFileText className="w-5 h-5 text-gray-400" />,
//                           },
//                           {
//                             label: 'Company ID',
//                             value: guestDetails.companyid || 'Not provided',
//                             icon: <FiFileText className="w-5 h-5 text-gray-400" />,
//                           },
//                         ]
//                       : []),
//                   ].map(({ label, value, icon }, index) => (
//                     <div key={index} className="bg-gray-50 rounded-lg p-3">
//                       <div className="flex items-center space-x-3">
//                         <div className="flex-shrink-0 bg-blue-100 p-2 rounded-lg">{icon}</div>
//                         <div className="flex-1 min-w-0">
//                           <p className="text-sm font-medium text-gray-500">{label}</p>
//                           <p
//                             className="text-sm font-medium text-gray-900 mt-1 truncate"
//                             title={value}
//                           >
//                             {value}
//                           </p>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Edit Modal */}
//           {isEditModalOpen && (
//             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//               <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
//                 <div className="flex justify-between items-center border-b p-4 sticky top-0 bg-white z-10">
//                   <h3 className="text-xl font-semibold text-gray-900">
//                     Update Profile Information
//                   </h3>
//                   <button onClick={closeEditModal} className="text-gray-500 hover:text-gray-700">
//                     <FiX size={24} />
//                   </button>
//                 </div>
//                 <form onSubmit={handleSubmit} className="p-6">
//                   <div className="space-y-4">
//                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           First Name *
//                         </label>
//                         <input
//                           type="text"
//                           name="firstname"
//                           value={formData.firstname}
//                           onChange={handleInputChange}
//                           className={`w-full p-2 border capitalize ${
//                             validationErrors.firstname ? 'border-red-500' : 'border-gray-300'
//                           } rounded `}
//                           required
//                         />
//                         {validationErrors.firstname && (
//                           <p className="text-red-500 text-xs mt-1">{validationErrors.firstname}</p>
//                         )}
//                       </div>

//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Last Name *
//                         </label>
//                         <input
//                           type="text"
//                           name="lastname"
//                           value={formData.lastname}
//                           onChange={handleInputChange}
//                           className={`w-full p-2 border capitalize ${
//                             validationErrors.lastname ? 'border-red-500' : 'border-gray-300'
//                           } rounded `}
//                           required
//                         />
//                         {validationErrors.lastname && (
//                           <p className="text-red-500 text-xs mt-1">{validationErrors.lastname}</p>
//                         )}
//                       </div>

//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Email
//                         </label>
//                         <input
//                           type="email"
//                           name="emailid"
//                           value={formData.emailid}
//                           onChange={handleInputChange}
//                           className="w-full p-2 border border-gray-300 rounded"
//                         />
//                         {formData.emailid !== guestDetails?.emailid && formData.emailid && (
//                           <p className="text-xs text-blue-600 mt-1">
//                             Verification OTP will be sent to this email
//                           </p>
//                         )}
//                       </div>

//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Phone Number *
//                         </label>
//                         <input
//                           type="tel"
//                           name="phonenumber"
//                           value={formData.phonenumber}
//                           onChange={handleInputChange}
//                           className="w-full p-2 border border-gray-300 rounded"
//                           required
//                         />
//                         {cleanPhoneNumber(formData.phonenumber) !==
//                           cleanPhoneNumber(guestDetails?.phonenumber) &&
//                           formData.phonenumber && (
//                             <p className="text-xs text-blue-600 mt-1">
//                               Verification OTP will be sent to this number
//                             </p>
//                           )}
//                       </div>

//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Country
//                         </label>
//                         <div className="relative">
//                           <button
//                             type="button"
//                             onClick={() => setIsCountryOpen(!isCountryOpen)}
//                             className="w-full flex items-center justify-between p-2 border border-gray-300 rounded-md text-sm bg-white"
//                           >
//                             <span className="flex items-center gap-2">
//                               <img
//                                 src={selectedCountry.flag}
//                                 alt={selectedCountry.name}
//                                 className="w-5 h-4 object-cover rounded-sm"
//                               />
//                               {selectedCountry.name}
//                             </span>
//                             <span className="ml-2">▼</span>
//                           </button>

//                           {isCountryOpen && (
//                             <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto z-10">
//                               {countries.map((country) => (
//                                 <div
//                                   key={country.code}
//                                   onClick={() => {
//                                     setSelectedCountry(country);
//                                     setIsCountryOpen(false);
//                                     setFormData((prev) => ({
//                                       ...prev,
//                                       countrycode: country.dialCode,
//                                       country: country.name,
//                                     }));
//                                   }}
//                                   className="flex items-center gap-2 p-2 hover:bg-gray-100 cursor-pointer"
//                                 >
//                                   <img
//                                     src={country.flag}
//                                     alt={country.name}
//                                     className="w-5 h-4 object-cover rounded-sm"
//                                   />
//                                   {country.name}
//                                 </div>
//                               ))}
//                             </div>
//                           )}
//                         </div>
//                       </div>

//                       <div>
//                         <label className="block text-sm font-medium text-gray-700 mb-1">
//                           Profile Type
//                         </label>
//                         <select
//                           name="client_type"
//                           value={formData.client_type}
//                           onChange={handleInputChange}
//                           className="w-full p-2 border border-gray-300 rounded"
//                           required
//                         >
//                           <option value="Leisure">Leisure</option>
//                           <option value="Corporate">Corporate</option>
//                         </select>
//                       </div>
//                       {formData.client_type === 'Corporate' && (
//                         <>
//                           <div>
//                             <label className="block text-sm font-medium text-gray-700 mb-1">
//                               Company Name *
//                             </label>
//                             <div className="relative">
//                               <input
//                                 type="text"
//                                 name="companyname"
//                                 placeholder="Search company name..."
//                                 value={formData.companyname}
//                                 onChange={(e) => {
//                                   const value = e.target.value;
//                                   setFormData((prev) => ({
//                                     ...prev,
//                                     companyname: value,
//                                     companyid: '',
//                                   }));
//                                   setCompanySearchTerm(value);
//                                 }}
//                                 onBlur={() => {
//                                   setTimeout(() => setIsCompanyDropdownOpen(false), 200);
//                                 }}
//                                 onFocus={() => {
//                                   if (companySearchTerm && companySearchTerm.length > 0) {
//                                     setIsCompanyDropdownOpen(true);
//                                   }
//                                 }}
//                                 className="w-full p-2 border border-gray-300 rounded"
//                                 required
//                               />

//                               {/* Company Dropdown */}
//                               {isCompanyDropdownOpen && filteredCompanies.length > 0 && (
//                                 <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
//                                   {filteredCompanies.map((company) => (
//                                     <div
//                                       key={company.id}
//                                       onClick={() => {
//                                         setFormData((prev) => ({
//                                           ...prev,
//                                           companyid:
//                                             company.companyid || company.id?.toString() || '',
//                                           companyname: company.companyname,
//                                         }));
//                                         setCompanySearchTerm(company.companyname);
//                                         setIsCompanyDropdownOpen(false);
//                                         toast.success(`Selected: ${company.companyname}`);
//                                       }}
//                                       className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
//                                     >
//                                       <div className="font-medium text-sm">
//                                         {company.companyname}
//                                       </div>
//                                       <div className="text-xs text-gray-500">
//                                         ID: {company.companyid || company.id}
//                                         {company.gstnumber && ` | GST: ${company.gstnumber}`}
//                                       </div>
//                                     </div>
//                                   ))}
//                                 </div>
//                               )}

//                               {/* No Company Found Message */}
//                               {isCompanyDropdownOpen &&
//                                 companySearchTerm.length > 2 &&
//                                 filteredCompanies.length === 0 &&
//                                 allCompanies.length > 0 && (
//                                   <div className="absolute z-50 w-full mt-1 bg-yellow-50 border border-yellow-300 rounded-md shadow-lg p-3">
//                                     <div className="flex items-start gap-2">
//                                       <svg
//                                         className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0"
//                                         fill="none"
//                                         viewBox="0 0 24 24"
//                                         stroke="currentColor"
//                                       >
//                                         <path
//                                           strokeLinecap="round"
//                                           strokeLinejoin="round"
//                                           strokeWidth={2}
//                                           d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
//                                         />
//                                       </svg>
//                                       <div>
//                                         <p className="text-sm text-yellow-800 font-medium">
//                                           Company not found
//                                         </p>
//                                         <p className="text-xs text-yellow-700 mt-1">
//                                           ℹ️ If your company is not in the list, please contact the
//                                           front desk to register your company for future use.
//                                         </p>
//                                       </div>
//                                     </div>
//                                   </div>
//                                 )}
//                             </div>
//                             {formData.companyid && (
//                               <p className="text-xs text-green-600 mt-1">
//                                 ✓ Company ID: {formData.companyid}
//                               </p>
//                             )}
//                           </div>

//                           <div>
//                             <label className="block text-sm font-medium text-gray-700 mb-1">
//                               Company ID *
//                             </label>
//                             <input
//                               type="text"
//                               name="companyid"
//                               value={formData.companyid}
//                               onChange={handleInputChange}
//                               className={`w-full p-2 border border-gray-300 rounded ${formData.companyid ? 'bg-gray-50' : ''}`}
//                               placeholder="Enter company ID"
//                               required
//                               readOnly={!!formData.companyid}
//                               title={formData.companyid ? 'Auto-filled from company selection' : ''}
//                             />
//                             {formData.companyid && (
//                               <p className="text-xs text-green-600 mt-1">
//                                 ✓ Company ID auto-filled
//                               </p>
//                             )}
//                           </div>
//                         </>
//                       )}
//                     </div>

//                     <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
//                       <div className="flex items-start gap-2">
//                         <FiInfo className="w-5 h-5 text-blue-600 mt-0.5" />
//                         <div>
//                           <p className="text-sm font-medium text-blue-800">Important Note</p>
//                           <p className="text-xs text-blue-600 mt-1">
//                             Profile photo and Aadhar card updates can only be done by our front desk
//                             team. Please visit the reception for any changes.
//                           </p>
//                         </div>
//                       </div>
//                     </div>
//                   </div>

//                   <div className="mt-6 flex justify-end space-x-3">
//                     <button
//                       type="button"
//                       onClick={closeEditModal}
//                       className="px-4 py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300"
//                     >
//                       Cancel
//                     </button>
//                     <button
//                       type="submit"
//                       className="px-4 py-2 bg-black text-white font-semibold rounded-md hover:bg-gray-800 flex items-center gap-2"
//                       disabled={
//                         isLoading || validationErrors.firstname || validationErrors.lastname
//                       }
//                     >
//                       {isLoading ? (
//                         'Saving...'
//                       ) : (
//                         <>
//                           <FiCheck /> Save Changes
//                         </>
//                       )}
//                     </button>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           )}

//           {/* OTP Verification Modal */}
//           {showOtpModal && (
//             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//               <div className="bg-white rounded-xl shadow-2xl w-full max-w-md sm:max-w-lg overflow-hidden relative">
//                 <button
//                   onClick={() => {
//                     setShowOtpModal(false);
//                     setOtp('');
//                     setOtpError('');
//                     // Rollback changes when user closes OTP modal
//                     rollbackContactChanges();
//                     setPendingChanges(null);
//                   }}
//                   className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
//                 >
//                   <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M6 18L18 6M6 6l12 12"
//                     />
//                   </svg>
//                 </button>

//                 <div className="p-6 sm:p-8">
//                   <div className="text-center">
//                     <div className="mx-auto flex items-center justify-center h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-blue-200 mb-4">
//                       {otpType === 'email' ? (
//                         <FiMail className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600" />
//                       ) : (
//                         <FiPhone className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600" />
//                       )}
//                     </div>
//                     <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
//                       Verify Your {otpType === 'email' ? 'Email' : 'Phone Number'}
//                     </h2>
//                     <p className="text-gray-600 mb-6 text-sm sm:text-base">
//                       We've sent a 6-digit verification code to{' '}
//                       <span className="font-semibold text-black">{otpIdentifier}</span>
//                     </p>
//                   </div>

//                   <div className="mb-6">
//                     <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
//                       Enter verification code
//                     </label>
//                     <div className="flex justify-center space-x-2 sm:space-x-3">
//                       {[0, 1, 2, 3, 4, 5].map((index) => (
//                         <input
//                           key={index}
//                           type="text"
//                           maxLength="1"
//                           value={otp[index] || ''}
//                           onChange={(e) => {
//                             const value = e.target.value.replace(/\D/g, '');
//                             if (value === e.target.value) {
//                               const newOtp = otp.split('');
//                               newOtp[index] = value;
//                               setOtp(newOtp.join(''));
//                               if (value && index < 5) {
//                                 document.getElementById(`otp-input-${index + 1}`)?.focus();
//                               }
//                               if (otpError) setOtpError('');
//                             }
//                           }}
//                           onKeyDown={(e) => {
//                             if (e.key === 'Backspace' && !otp[index] && index > 0) {
//                               document.getElementById(`otp-input-${index - 1}`)?.focus();
//                             }
//                           }}
//                           id={`otp-input-${index}`}
//                           className={`w-10 h-10 sm:w-12 sm:h-12 text-xl sm:text-2xl text-center border ${
//                             otpError ? 'border-red-500' : 'border-gray-300'
//                           } rounded-md focus:outline-none focus:ring-2 focus:ring-black`}
//                           inputMode="numeric"
//                           pattern="[0-9]*"
//                         />
//                       ))}
//                     </div>
//                     {otpError && (
//                       <p className="mt-2 text-sm text-red-600 text-center">{otpError}</p>
//                     )}
//                   </div>

//                   <div className="flex flex-col space-y-3">
//                     <button
//                       onClick={handleVerifyOtp}
//                       className="w-full py-2.5 sm:py-3 px-4 bg-black hover:text-gray-300 text-white font-medium rounded-lg shadow transition-colors disabled:opacity-50"
//                       disabled={isVerifying || otp.length !== 6}
//                     >
//                       {isVerifying ? (
//                         <span className="flex items-center justify-center">
//                           <svg
//                             className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
//                             xmlns="http://www.w3.org/2000/svg"
//                             fill="none"
//                             viewBox="0 0 24 24"
//                           >
//                             <circle
//                               className="opacity-25"
//                               cx="12"
//                               cy="12"
//                               r="10"
//                               stroke="currentColor"
//                               strokeWidth="4"
//                             ></circle>
//                             <path
//                               className="opacity-75"
//                               fill="currentColor"
//                               d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                             ></path>
//                           </svg>
//                           Verifying...
//                         </span>
//                       ) : (
//                         'Verify and Update'
//                       )}
//                     </button>
//                   </div>
//                   <div className="mt-6 text-center text-xs sm:text-sm text-gray-500">
//                     Didn't receive code?{' '}
//                     {canResend ? (
//                       <button
//                         className="font-medium text-blue-600 hover:text-blue-500"
//                         onClick={handleResendOtp}
//                         disabled={isVerifying}
//                       >
//                         Resend OTP
//                       </button>
//                     ) : (
//                       <span className="text-gray-500">Resend OTP in {resendTimer} seconds</span>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}
//         </div>
//       )}

//       {activeTab === 'bookingHistory' && <BookingHistory bookings={bookings} />}
//       {activeTab === 'helpSupport' && <HelpSupportForm guestDetails={guestDetails} />}

//       <ToastContainer
//         position="top-right"
//         autoClose={5000}
//         hideProgressBar={false}
//         newestOnTop={true}
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="light"
//       />
//     </div>
//   );
// };

// export default Profile;
